import "./style.scss";

import { ButtonIconPosition, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, View } from "@amzn/stencil-react-components/layout";
import { WithModal } from "@amzn/stencil-react-components/modal";
import IconExternalLink from "@amzn/stencil-react-icons/icons/icon-external-link";
import Button from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { IItemVersion } from "lib/types/items";
import React, { FunctionComponent, ReactElement } from "react";

import { validateUrl } from "../../helpers";
import { getString } from "../../uistringlabels/uiStringUtils";
import { VersionObject } from "../SelectChildSku";
import { TeamAccessRequestModal } from "../TeamAccessRequestModal";

/**
 * Props interface for the ParentChildActions component
 * Defines all the properties needed to render parent-child product actions
 */
export interface ParentChildActionsProps {
  /**
   * Product name for display in UI elements and modals
   */
  productName: string;

  /**
   * Available teams/versions for team access requests
   * Array of item versions representing different teams that can be requested
   */
  availableTeams: IItemVersion[];

  /**
   * Whether the current user has an existing license or access to the product
   * Controls visibility of certain action buttons
   */
  hasLicense?: boolean;

  /**
   * Product launch URL for external redirection
   * Used when user clicks the launch button to open the product
   */
  productDownloadUrl?: string;

  /**
   * Callback function to handle team access order requests
   * Called when user selects a team and confirms the order
   *
   * @param selectedTeam - The team/version object selected by the user
   */
  onOrderTeam: (selectedTeam: VersionObject) => void;

  /**
   * Optional callback function to open the main order modal
   * Called after team selection to trigger the standard order flow
   */
  onOrderModalOpen?: () => void;

  /**
   * Whether any action is currently in a loading state
   * Disables buttons and shows loading indicators when true
   */
  isLoading?: boolean;

  /**
   * Optional return button component to be rendered
   * Pre-configured return button passed from parent component
   */
  returnButtonComponent?: ReactElement;
}

/**
 * ParentChildActions Component
 *
 * Renders action buttons and controls for parent-child products, which are products
 * that have multiple team versions (like Canva teams or Confluence teams) sharing
 * the same product instance but requiring separate access requests.
 *
 * Features:
 * - Launch button for users with existing licenses
 * - Team access request functionality for additional teams
 * - Return button integration when applicable
 * - Loading states and proper URL validation
 *
 * @param props - Component props as defined in ParentChildActionsProps
 * @returns JSX element containing the parent-child product actions
 */
export const ParentChildActions: FunctionComponent<ParentChildActionsProps> = ({
  productName,
  availableTeams,
  hasLicense = false,
  productDownloadUrl,
  onOrderTeam,
  onOrderModalOpen,
  isLoading = false,
  returnButtonComponent,
}) => {
  /**
   * Opens a URL in a new browser tab with security considerations
   *
   * @param url - The URL to open in a new tab
   */
  const openExternalTab = (url: string): void => {
    window.open(url, "_blank");
  };

  /**
   * Handles the launch button click event
   * Validates and opens the product URL in a new tab using enhanced URL validation
   */
  const handleLaunchClick = (): void => {
    if (productDownloadUrl) {
      // Use enhanced URL validation for security
      const validatedUrl = validateUrl(productDownloadUrl);
      openExternalTab(validatedUrl);
    }
  };

  /**
   * Renders the team access request modal with proper event handling
   *
   * @param close - Function to close the modal
   * @returns JSX element for the team access modal
   */
  const renderTeamAccessModal = ({ close }: { close: () => void }): JSX.Element => {
    return (
      <TeamAccessRequestModal
        isOpen={true}
        onClose={close}
        onOrderTeam={(selectedTeam) => {
          onOrderTeam(selectedTeam);
          close();
          // Trigger the order modal after team selection and modal close
          if (onOrderModalOpen) {
            setTimeout(() => {
              onOrderModalOpen();
            }, 100); // Small delay to ensure modal is closed first
          }
        }}
        availableTeams={availableTeams}
        productName={productName}
        isLoading={isLoading}
      />
    );
  };

  return (
    <View className="parent-child-actions">
      <Col alignItems="start">
        <View className="parent-child-actions__buttons" style={{ marginTop: "16px", width: "100%" }}>
          {/* Launch Button - only show if user has license */}
          {hasLicense && productDownloadUrl && (
            <Button
              variant={ButtonVariant.Primary}
              icon={<IconExternalLink aria-hidden={true} />}
              iconPosition={ButtonIconPosition.Trailing}
              onClick={handleLaunchClick}
              disabled={isLoading}
              data-cy="launch-button"
              data-testid="launch-button"
              touchpoint="launch-button"
              style={{ minWidth: "120px", marginBottom: "12px" }}
            >
              {getString("ordering.launch")}
            </Button>
          )}

          {/* Request Additional Team Access Button - only show if user has license */}
          {hasLicense && (
            <WithModal renderModal={renderTeamAccessModal} isScrollable={false}>
              {({ open }: { open: () => void }) => (
                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={open}
                  disabled={isLoading}
                  data-cy="request-team-access-button"
                  data-testid="request-team-access-button"
                  touchpoint="request-team-access-button"
                  style={{ minWidth: "200px" }}
                >
                  {getString("teamAccess.requestButton")}
                </Button>
              )}
            </WithModal>
          )}

          {/* Return Button - only show if user has license */}
          {hasLicense && <div style={{ marginBottom: "12px" }}>{returnButtonComponent}</div>}
        </View>
      </Col>
    </View>
  );
};
